import { blacklistedTags } from "../config/blacklistedTags";
import { HighlightStore } from "$/stores/HighlightStore";
import { PreferencesStore } from "$/stores/global/PreferencesStore";
import { get } from "svelte/store";
import { visualizeSnippets } from "$/utils/CustomLocateV4/marker/index";
import { ActiveTopic } from "$/stores/extension/ActiveTopicStore";
import { KnowledgeTriggerStore } from "$/stores/KnowledgeTriggerStore";
import { isPdfViewerUrl } from "$/utils/Utils";
export function findTextRanges(texts) {
    const locale = document.documentElement.lang || undefined;
    const find = texts.map((text) => text.trim().toLocaleLowerCase(locale));
    const state = find.map(() => ({ start: null, end: null, shift: 0 }));
    const ranges = find.map(() => []);
    const treeWalker = document.createTreeWalker(document.body, 
    //only text nodes
    NodeFilter.SHOW_TEXT, 
    //skip blacklisted tags
    (node) => {
        var _a, _b;
        return blacklistedTags.includes((_a = node.parentNode) === null || _a === void 0 ? void 0 : _a.tagName) || ((_b = node.parentNode) === null || _b === void 0 ? void 0 : _b.contentEditable) == "true"
            ? // || !(node.parentNode as HTMLElement)?.checkVisibility() //ignore visiblity, maybe it inside section
                NodeFilter.FILTER_REJECT
            : NodeFilter.FILTER_ACCEPT;
    });
    let node;
    while ((node = treeWalker.nextNode())) {
        if (!(node === null || node === void 0 ? void 0 : node.nodeValue))
            continue;
        //iterate over text node chars
        for (let i = 0; i < node.nodeValue.length; i++) {
            const char = node.nodeValue[i].toLocaleLowerCase(locale).trim();
            //skip spaces
            if (!char)
                continue;
            //search all texts simultaneously
            find.forEach((text, j) => {
                var _a;
                //skip spaces
                while (text[state[j].shift] && !text[state[j].shift].trim())
                    state[j].shift++;
                let matched = text[state[j].shift] === char;
                //maybe previous node have been a mistake?
                if (!matched && state[j].shift) {
                    state[j].shift = 0;
                    matched = text[state[j].shift] === char;
                }
                if (matched) {
                    //start (first char match)
                    if (!state[j].shift)
                        state[j].start = [node, i];
                    state[j].end = [node, i];
                    //move to next char
                    state[j].shift++;
                }
                //end (enire text match)
                if (state[j].shift >= text.length) {
                    const range = document.createRange();
                    range.setStart(state[j].start[0], state[j].start[1]);
                    range.setEnd(state[j].end[0], state[j].end[1] + 1);
                    if (!range.collapsed &&
                        (
                        //@ts-ignore
                        (_a = range.commonAncestorContainer.parentElement) === null || _a === void 0 ? void 0 : _a.checkVisibility()))
                        ranges[j].push(range);
                    else
                        range.detach();
                    //complete
                    matched = false;
                }
                //mismatch or complete, restart to find next match
                if (!matched) {
                    state[j].shift = 0;
                    state[j].start = null;
                    state[j].end = null;
                }
            });
        }
    }
    return ranges;
}
export function htmlToText(content) {
    const div = document.createElement("div");
    div.innerHTML = content;
    const text = div.innerText;
    div.remove();
    return text;
}
export function getElementAndTextNode(selector, offset) {
    // Remove the textNode part from the selector string
    let modifiedSelector = selector.replace(/>textNode:nth-of-type\(\d+\)$/, "");
    // Select the parent element using the modified selector string
    let parentElement = null;
    try {
        parentElement = document.querySelector(modifiedSelector);
    }
    catch (error) {
        // if Failed to execute 'querySelector' on 'Document': '#17th_century' is not a valid selector.
        if (error instanceof DOMException &&
            error.name === "SyntaxError" &&
            error.message.includes("is not a valid selector")) {
            parentElement = document.getElementById(modifiedSelector.replace("#", ""));
        }
        else {
            console.error("Error selecting parent element", error);
            return null;
        }
    }
    // Check if the parent element exists
    if (parentElement) {
        // Extract the index of the textNode from the original selector
        const textNodeIndexMatch = selector.match(/>textNode:nth-of-type\((\d+)\)$/);
        const textNodeIndex = textNodeIndexMatch
            ? parseInt(textNodeIndexMatch[1], 10)
            : 0;
        // Navigate to the textNode within the parent element
        const textNode = parentElement.childNodes[textNodeIndex];
        // Check if the textNode exists
        if (!textNode) {
            console.error("Text node not found", "index:", textNodeIndex, "parentElement:", parentElement);
            return null;
        }
        // If textNode is not really a textNode, (e.g. it's a img/element node),
        // find the next or previous text node so that we can use it as the textNode for the highlight
        const remappedNode = remapNode(textNode, offset);
        return remappedNode; // returns { node, offset }
    }
    else {
        console.error("Element for selector not found", selector);
        return null;
    }
}
export function getAllSnippetsFromSameUrl(snippets, url) {
    let snippetsFromSameUrl = [];
    snippets.forEach((snippet) => {
        let cleanSnippetUrl = decodeURIComponent(snippet.url.replace(/#:sid:.*/g, "").replace(/#:~:.*$/g, ""));
        let cleanUrl = decodeURIComponent(url.replace(/#:sid:.*/g, ""));
        if (cleanSnippetUrl === cleanUrl) {
            snippetsFromSameUrl.push(snippet);
        }
    });
    return snippetsFromSameUrl;
}
export function mapSameUrlSnippetsToHighlights(snippets, url) {
    if (!snippets || !snippets.length)
        return [];
    const snippetsFromSameUrl = getAllSnippetsFromSameUrl(snippets, url);
    const highlights = snippetsFromSameUrl.map((snippet) => mapSnippetToHighlight(snippet));
    // if highlight does not have anchorNode or focusNode, its a bookmark and should be removed
    return highlights.filter((highlight) => highlight.highlight.anchorNode && highlight.highlight.focusNode);
}
export function mapSnippetToHighlight(snippet) {
    var _a;
    const { id, content, url, group, highlight, originalId } = snippet;
    return {
        id: id,
        top: 0,
        left: 0,
        width: 0,
        height: 0,
        group: group,
        url: url || "",
        content: content || "",
        highlight: {
            anchorNode: (highlight === null || highlight === void 0 ? void 0 : highlight.anchorNode) || "",
            anchorOffset: (highlight === null || highlight === void 0 ? void 0 : highlight.anchorOffset) || 0,
            container: (highlight === null || highlight === void 0 ? void 0 : highlight.container) || "",
            focusNode: (highlight === null || highlight === void 0 ? void 0 : highlight.focusNode) || "",
            focusOffset: (highlight === null || highlight === void 0 ? void 0 : highlight.focusOffset) || 0,
            pageNumber: (_a = highlight === null || highlight === void 0 ? void 0 : highlight.pageNumber) !== null && _a !== void 0 ? _a : null,
            postContent: (highlight === null || highlight === void 0 ? void 0 : highlight.postContent) || "",
            preContent: (highlight === null || highlight === void 0 ? void 0 : highlight.preContent) || "",
        },
        originalId: originalId,
    };
}
export function remapNode(element, offset) {
    var _a;
    if (element.nodeType !== Node.TEXT_NODE) {
        // Try to find the next text node in the sibling or the parent's sibling
        let nextTextNode = findNextTextNode(element);
        if (nextTextNode) {
            return { node: nextTextNode, offset: 0 };
        }
        // Try to find the previous text node in the sibling or the parent's sibling
        let previousTextNode = findPreviousTextNode(element);
        if (previousTextNode) {
            return {
                node: previousTextNode,
                offset: ((_a = previousTextNode.textContent) === null || _a === void 0 ? void 0 : _a.length) || 0,
            };
        }
        console.error("No text node found");
        return { node: null, offset: 0 };
    }
    // Return if the element is already a text node
    return { node: element, offset };
}
export function findNextTextNode(element) {
    let next = element.nextSibling;
    // Traverse through the sibling nodes
    while (next) {
        if (next.nodeType === Node.TEXT_NODE) {
            return next;
        }
        // If the sibling has child nodes, search within the first child
        if (next.childNodes.length) {
            let firstChild = findFirstTextNode(next);
            if (firstChild)
                return firstChild;
        }
        next = next.nextSibling;
    }
    // If no next sibling, go up the parent and check the parent's sibling
    let parent = element.parentNode;
    while (parent && parent !== document.body) {
        next = parent.nextSibling;
        while (next) {
            if (next.nodeType === Node.TEXT_NODE) {
                return next;
            }
            if (next.childNodes.length) {
                let firstChild = findFirstTextNode(next);
                if (firstChild)
                    return firstChild;
            }
            next = next.nextSibling;
        }
        parent = parent.parentNode;
    }
    return null;
}
export function findPreviousTextNode(element) {
    let prev = element.previousSibling;
    // Traverse through the previous siblings
    while (prev) {
        if (prev.nodeType === Node.TEXT_NODE) {
            return prev;
        }
        // If the sibling has child nodes, search within the last child
        if (prev.childNodes.length) {
            let lastChild = findLastTextNode(prev);
            if (lastChild)
                return lastChild;
        }
        prev = prev.previousSibling;
    }
    // If no previous sibling, go up the parent and check the parent's previous sibling
    let parent = element.parentNode;
    while (parent && parent !== document.body) {
        prev = parent.previousSibling;
        while (prev) {
            if (prev.nodeType === Node.TEXT_NODE) {
                return prev;
            }
            if (prev.childNodes.length) {
                let lastChild = findLastTextNode(prev);
                if (lastChild)
                    return lastChild;
            }
            prev = prev.previousSibling;
        }
        parent = parent.parentNode;
    }
    return null;
}
// Helper function to find the first text node in a subtree
function findFirstTextNode(element) {
    let current = element;
    while (current) {
        if (current.nodeType === Node.TEXT_NODE) {
            return current;
        }
        if (current.childNodes.length) {
            current = current.childNodes[0];
        }
        else {
            break;
        }
    }
    return null;
}
// Helper function to find the last text node in a subtree
function findLastTextNode(element) {
    let current = element;
    while (current) {
        if (current.nodeType === Node.TEXT_NODE) {
            return current;
        }
        if (current.childNodes.length) {
            current = current.childNodes[current.childNodes.length - 1];
        }
        else {
            break;
        }
    }
    return null;
}
export function cleanup(styleId) {
    var _a;
    (_a = document.getElementById(styleId)) === null || _a === void 0 ? void 0 : _a.remove();
    HighlightStore.clearHighlightsByGroup(styleId);
}
export function cleanupByHighlightId(id) {
    // regex for ::highlight(${id}) to remove the highlight rule
    const highlightRulePattern = new RegExp(`::highlight\\(cw-${id}\\)\\s*{[^}]*}`, "g");
    // regex for --highlight-${id}-top:.*?%; to remove the custom property from :root
    const rootVariablePattern = new RegExp(`--highlight-${id}-top:.*?%;`, "g");
    // Function to clean up styles by replacing content in innerHTML
    function removeHighlightRule(styleElement) {
        if (!styleElement)
            return;
        styleElement.innerHTML = styleElement.innerHTML
            .replace(highlightRulePattern, "")
            .replace(rootVariablePattern, "");
    }
    // must clean both styles
    removeHighlightRule(document.getElementById("cw-current"));
    removeHighlightRule(document.getElementById("cw-all"));
    HighlightStore.deleteHighlight(id);
}
function performCleanup(groupId) {
    cleanup(groupId);
    document.querySelectorAll('[id^="tooltip-"]').forEach((el) => el.remove());
    document.querySelectorAll("style[data-hover]").forEach((el) => el.remove());
    // Remove any existing hover background reset styles
    document.querySelectorAll('style[data-hover-reset]').forEach(el => el.remove());
    // Add a style to reset hover background colors only for highlights
    const resetStyle = document.createElement('style');
    resetStyle.setAttribute('data-hover-reset', 'true');
    resetStyle.textContent = `
        ::highlight([id^="cw-"]) {
            background-color: transparent !important;
        }
    `;
    document.head.appendChild(resetStyle);
    document.querySelectorAll('style').forEach(style => {
        var _a;
        if ((_a = style.textContent) === null || _a === void 0 ? void 0 : _a.includes('::highlight')) {
            style.remove();
        }
    });
    // @ts-ignore
    CSS.highlights.clear();
}
export function cleanupAllHighlights() {
    performCleanup("cw-current");
    performCleanup("cw-all");
}
export function cleanupAllTriggers() {
    var _a;
    (_a = document.getElementById("cw-trigger")) === null || _a === void 0 ? void 0 : _a.remove();
    HighlightStore.clearHighlightsByGroup("cw-trigger");
    document.querySelectorAll('[id^="tooltip-cw-"]').forEach(el => el.remove());
    document.querySelectorAll('style[data-hover]').forEach(el => {
        var _a;
        if ((_a = el.textContent) === null || _a === void 0 ? void 0 : _a.includes('cw-')) {
            el.remove();
        }
    });
    document.querySelectorAll('style[data-hover-reset]').forEach(el => el.remove());
    // Only reset hover styles for trigger highlights
    const resetStyle = document.createElement('style');
    resetStyle.setAttribute('data-hover-reset', 'true');
    resetStyle.textContent = `
        ::highlight([id^="cw-"]) {
            background-color: transparent !important;
            background-image: none !important;
            background: transparent !important;
            background-clip: unset !important;
            background-origin: unset !important;
            background-position: unset !important;
            background-repeat: unset !important;
            background-size: unset !important;
            background-attachment: unset !important;
        }
    `;
    document.head.appendChild(resetStyle);
    document.querySelectorAll('style').forEach(style => {
        var _a;
        if ((_a = style.textContent) === null || _a === void 0 ? void 0 : _a.includes('::highlight(cw-')) {
            style.remove();
        }
    });
    if (CSS.highlights) {
        // @ts-ignore
        CSS.highlights.clear();
    }
}
export function isHighlightImageOnly(highlight) {
    var _a;
    const content = highlight.content;
    const text = htmlToText(content);
    return content.includes("<img") && !((_a = text.trim()) === null || _a === void 0 ? void 0 : _a.length);
}
export function findImageBySrcOrAlt(imgString) {
    var _a, _b;
    const imgSrc = (_a = imgString.match(/src="([^"]*)"/)) === null || _a === void 0 ? void 0 : _a[1];
    const imgAlt = (_b = imgString.match(/alt="([^"]*)"/)) === null || _b === void 0 ? void 0 : _b[1];
    if (!imgSrc && !imgAlt)
        return null;
    // try to find the image by src attribute
    let imgElement = document.querySelector(`img[src="${imgSrc}"]`);
    // try to find the image by alt attribute
    if (!imgElement)
        imgElement = document.querySelector(`img[alt="${imgAlt}"]`);
    if (!imgElement) {
        return null;
    }
    return imgElement;
}
export async function highlightSnippets() {
    var _a, _b, _c, _d;
    const url = window.location.origin + window.location.pathname;
    cleanupAllTriggers();
    cleanupAllHighlights();
    if (!isPdfViewerUrl(url)) {
        const snippetMap = new Map();
        if ((_a = get(PreferencesStore)) === null || _a === void 0 ? void 0 : _a.betaFeatures) {
            let triggers = KnowledgeTriggerStore.getTriggers(location.href);
            if (triggers.length === 0) {
                triggers = await KnowledgeTriggerStore.fetchTriggers(location.href, document);
            }
            let snippets = ((_b = get(ActiveTopic)) === null || _b === void 0 ? void 0 : _b.snippets) || [];
            const snippetIds = new Set(snippets.map(s => s.id));
            const snippetOriginalIds = new Set(snippets.map(s => s.originalId).filter(Boolean));
            for (const trigger of triggers) {
                if (snippetIds.has(trigger.id) || snippetOriginalIds.has(trigger.id)) {
                    continue;
                }
                snippetMap.set(trigger.id, Object.assign(Object.assign({}, trigger), { group: "trigger" }));
            }
        }
        if ((_c = get(PreferencesStore)) === null || _c === void 0 ? void 0 : _c.showAllHighlights) {
            let snippets = ((_d = get(ActiveTopic)) === null || _d === void 0 ? void 0 : _d.snippets) || [];
            for (const snippet of snippets) {
                snippetMap.set(snippet.id, Object.assign(Object.assign({}, snippet), { group: "highlight" }));
            }
        }
        const array = Array.from(snippetMap.values());
        visualizeSnippets(array, url);
    }
}
export async function highlightTriggers() {
    var _a;
    const url = window.location.origin + window.location.pathname;
    if (!isPdfViewerUrl(url)) {
        const snippetMap = new Map();
        if ((_a = get(PreferencesStore)) === null || _a === void 0 ? void 0 : _a.betaFeatures) {
            let triggers = KnowledgeTriggerStore.getTriggers(location.href);
            if (triggers.length === 0) {
                triggers = await KnowledgeTriggerStore.fetchTriggers(location.href, document);
            }
            for (const trigger of triggers) {
                snippetMap.set(trigger.id, Object.assign(Object.assign({}, trigger), { group: "trigger" }));
            }
        }
        const array = Array.from(snippetMap.values());
        visualizeSnippets(array, url);
    }
}
export function isBlogPostOrArticle(document) {
    var _a, _b, _c;
    // Check if Document exists and it's actually Document
    if (!document || !(document instanceof Document)) {
        return false;
    }
    // Check for Open Graph type article
    const ogType = (_a = document
        .querySelector('meta[property="og:type"]')) === null || _a === void 0 ? void 0 : _a.getAttribute("content");
    if (ogType === "article") {
        return true;
    }
    // Check for Open Graph article author
    const ogAuthor = (_b = document
        .querySelector('meta[property="og:article:author"]')) === null || _b === void 0 ? void 0 : _b.getAttribute("content");
    if (ogAuthor) {
        return true;
    }
    // Check for other author-related meta tags
    const authorMeta = (_c = document
        .querySelector('meta[name="author"]')) === null || _c === void 0 ? void 0 : _c.getAttribute("content");
    if (authorMeta) {
        return true;
    }
    // Check for article-related structured data or schema.org markup
    const articleSchema = document.querySelector('script[type="application/ld+json"]');
    if (articleSchema) {
        try {
            const jsonData = JSON.parse(articleSchema.textContent || "");
            if (jsonData["@type"] === "Article" ||
                jsonData["@type"] === "BlogPosting") {
                return true;
            }
        }
        catch (e) {
            // Ignore JSON parsing errors
        }
    }
    // Check for common article/blog post indicators in HTML structure
    const articleElement = document.querySelector("article");
    if (articleElement) {
        return true;
    }
    return false;
}
